import i18next from 'i18next';
import {ActivityType} from '../../../../../backend/interfaces/activity-type';
import {HealthMeasurement} from '../../../../../backend/interfaces/health-measurement';
import {HealthProfile} from '../../../../../backend/interfaces/health-profile';
import {translations} from '../../../../../i18n/translations';
import {createTextByCareTaskFeature} from '../../care-and-activities/export-data-care-and-activities';
import {exportCarePlanProcessReportData} from '../../care-plan/base/export-care-plan-process-report-data';
import {
  ExportSeniorProfileData,
  ExportSeniorProfileLatestVitalSignsData,
  ExportSeniorProfileReportData,
} from './export-senior-profile-types';

export function exportSeniorProfileProcessReportData(
  reportData: ExportSeniorProfileReportData
): ExportSeniorProfileData {
  const foodCarePlanData = reportData.foodReportData
    ? exportCarePlanProcessReportData(reportData.foodReportData)
    : undefined;
  const medicationCarePlanData = reportData.medicationReportData
    ? exportCarePlanProcessReportData(reportData.medicationReportData)
    : undefined;

  const userHealthConditions = processUserHealthProfileData(reportData.userData.healthProfile);
  const userLastVitalSings = processVitalSignsData(
    reportData.vitalSignsCarePlanData.groupActivitiesTypesData,
    reportData.userData.latestHealthMeasurements
  );

  return {
    foodCarePlanData,
    medicationCarePlanData,
    mealActivityTypes: reportData.mealsActivityTypesData,
    userData: {
      user: reportData.userData.user,
      healthConditions: userHealthConditions,
      lastestVitalSigns: userLastVitalSings,
    },
  };
}

// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------

function processVitalSignsData(
  vitalSignsActivityTypes: ActivityType[],
  latestHealthMeasurements: HealthMeasurement[]
): ExportSeniorProfileLatestVitalSignsData[] {
  const result: ExportSeniorProfileLatestVitalSignsData[] = [];

  for (const measurement of latestHealthMeasurements) {
    const matchingActivityType = vitalSignsActivityTypes.find(
      (type) => type.healthMeasurementType === measurement.type
    );

    if (!matchingActivityType) {
      console.warn(
        `No matching activity type found for health measurement type ${measurement.type}`
      );
      continue;
    }

    const registerText = createTextByCareTaskFeature(
      i18next.t,
      measurement.data,
      matchingActivityType.careTaskFeature
    );

    result.push({
      measurement,
      activityType: matchingActivityType,
      registerText: registerText ?? '-',
    });
  }

  return result;
}

function processUserHealthProfileData(userHealthProfile: HealthProfile | null): string[] {
  if (!userHealthProfile) return [];

  const conditions = [
    userHealthProfile.hasVisualDificulty
      ? i18next.t(translations.health_profile_form.visual_difficulty_label)
      : null,

    userHealthProfile.hasAlzheimer
      ? i18next.t(translations.health_profile_form.alzheimer_label)
      : null,

    userHealthProfile.hasHearingDificulty
      ? i18next.t(translations.health_profile_form.hearing_difficulty_label)
      : null,

    userHealthProfile.hasCognitiveDeficit
      ? i18next.t(translations.health_profile_form.cognitive_deficit_label)
      : null,

    userHealthProfile.hasParkinson
      ? i18next.t(translations.health_profile_form.parkinson_label)
      : null,

    userHealthProfile.hasOtherDementias
      ? i18next.t(translations.health_profile_form.other_dementias_label)
      : null,

    userHealthProfile.hasReducedMobilityUpperLimbs
      ? i18next.t(translations.health_profile_form.reduced_mobility_upper_limbs_label)
      : null,

    userHealthProfile.hasReducedMobilityLowerLimbs
      ? i18next.t(translations.health_profile_form.reduced_mobility_lower_limbs_label)
      : null,

    userHealthProfile.hasParalysisLeftLimb
      ? i18next.t(translations.health_profile_form.paralysis_left_limb_label)
      : null,

    userHealthProfile.hasParalysisRightLimb
      ? i18next.t(translations.health_profile_form.paralysis_right_limb_label)
      : null,

    userHealthProfile.usesWheelchair
      ? i18next.t(translations.health_profile_form.wheelchair_label)
      : null,

    userHealthProfile.isIlliterate
      ? i18next.t(translations.health_profile_form.illiterate_label)
      : null,
  ];

  const activeConditions = conditions.filter((c): c is string => c !== null);

  return activeConditions;
}
